import { useCallback } from 'react'
import { CreditApplication, CreditApplicationStatus, MajorAccountChange } from '_gql'

import useGetIsLoggedInAsAuthSigner from '@sputnik/hooks/useGetIsLoggedInAsAuthSigner'
import { MAJOR_ACCOUNT_CHANGE, MIGRATION } from '@sputnik/resources/constants'

import { useGetCreditApplicationTypes } from './GetCreditApplicationTypes.gql'

interface UseGetCreditAppTypesProps {
  salesforceCreditId: CreditApplication['salesforceCreditId']
}

const editableStatuses = [
  CreditApplicationStatus.IN_PROGRESS,
  CreditApplicationStatus.CORRECTION_REQUESTED,
  CreditApplicationStatus.NOT_STARTED,
  CreditApplicationStatus.AWAITING_SIGNATURE,
]

const latestAppVersion = 3

const useGetCreditAppTypes = ({ salesforceCreditId }: UseGetCreditAppTypesProps) => {
  const { data, loading: isLoadingCreditAppTypes } = useGetCreditApplicationTypes({
    skip: !salesforceCreditId,
    variables: { creditApplicationId: salesforceCreditId },
  })

  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail: data?.creditApplication?.authorizedSigner?.email ?? '',
  })

  const { appVersion, recordType, majorAccountChanges, status } = data?.creditApplication || {}

  const isLatestAppVersion = appVersion === latestAppVersion

  const isMajorAccountChange = recordType === MAJOR_ACCOUNT_CHANGE
  const isMigration = recordType === MIGRATION
  const isEditable = editableStatuses.some((s) => {
    if (s === CreditApplicationStatus.AWAITING_SIGNATURE) {
      return s === status && isLoggedInUserAuthorizedSigner
    }
    return s === status
  })

  const hasMajorAccountChange = useCallback(
    (...requests: MajorAccountChange[]) => {
      if (!isMajorAccountChange) {
        return false
      }
      if (!majorAccountChanges?.length) {
        return false
      }

      return requests.some((request) => majorAccountChanges?.some((change) => change === request))
    },
    [majorAccountChanges, isMajorAccountChange],
  )

  return {
    isLatestAppVersion,
    isLoadingCreditAppTypes,
    isMajorAccountChange,
    isMigration,
    hasMajorAccountChange,
    isEditable,
  }
}

export default useGetCreditAppTypes
